<script setup lang="ts">
import { computed } from "vue";
import "@maersk-global/mds-components-core/mc-icon";
import { useSidebarState } from "@/compositions/useSidebarState";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { ROUTES } from "@/router/constants";
import { translationKeyTransform } from "@/utils/translations-key-transformation";
import type { NavigationLink } from "@/components/SideNavigation";

const props = defineProps<{
  links: NavigationLink[];
}>();

const { t } = useI18n();
const { isCollapsed, toggle } = useSidebarState();
const route = useRoute();

const getImageUrl = (name: string) => new URL(`../../assets/${name}`, import.meta.url).href;

const prefixMatchRoutes = new Set([
  ROUTES.RISK_MANAGEMENT,
  ROUTES.REPORTING,
  ROUTES.CARRIER_PERFORMANCE,
  // Track & Trace renders its tabs as child routes (/track-and-trace/shipments, ...).
  ROUTES.TRACK_AND_TRACE
]);

const activeLink = computed<NavigationLink | undefined>(() =>
  props.links.find((link) =>
    prefixMatchRoutes.has(link.route) ? route.path.startsWith(link.route) : route.path === link.route
  )
);

const pageTitle = computed(() => (activeLink.value ? t(translationKeyTransform(activeLink.value.name)) : ""));
</script>

<template>
  <header class="app-header" data-test="app-header/root">
    <div class="app-header__hamburger-zone">
      <button
        class="app-header__hamburger"
        :aria-label="isCollapsed ? t('SIDEBAR.EXPAND') : t('SIDEBAR.COLLAPSE')"
        :aria-expanded="!isCollapsed"
        aria-controls="app-sidebar"
        data-test="app-header/hamburger"
        @click="toggle"
      >
        <mc-icon icon="bars-horizontal" size="20" />
      </button>
    </div>

    <div class="app-header__brand" data-test="app-header/brand">
      <img :src="getImageUrl('Star.svg')" alt="Maersk" class="app-header__brand-logo" width="20" height="20" />
      <div class="app-header__brand-divider" aria-hidden="true" />
      <span class="app-header__app-name">Maersk Visibility Studio</span>
      <template v-if="pageTitle">
        <span class="app-header__breadcrumb-sep" aria-hidden="true">/</span>
        <span class="app-header__page-title" :title="pageTitle">{{ pageTitle }}</span>
      </template>
    </div>
  </header>
</template>

<style scoped lang="scss">
@import "@/styles/colors_mds.scss";

.app-header {
  display: flex;
  align-items: center;
  height: 56px;
  background-color: $wrapper--mds_brand_appearance_neutral_default_background-color;
  border-block-end: 1px solid $wrapper--mds_brand_appearance_neutral_weak_background-color;
  position: relative;
  z-index: 1000;

  &__hamburger-zone {
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__hamburger {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--mds_brand_border_large_radius, 8px);
    background: transparent;
    cursor: pointer;
    transition: background-color var(--mds_global_transition_fast_duration, 0.2s) ease-in-out;

    mc-icon::part(icon) {
      fill: $wrapper--mds_brand_appearance_neutral_default_text-color;
    }

    &:hover {
      background-color: $wrapper--mds_brand_appearance_neutral_weak_background-color;
    }

    &:focus-visible {
      outline: 2px solid $wrapper--mds_brand_appearance_primary_weak_background-color;
      outline-offset: 2px;
    }

    &:active {
      transform: scale(0.94);
    }
  }

  &__brand {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    padding-inline-start: 4px;
  }

  &__brand-logo {
    display: block;
    flex-shrink: 0;
    margin-inline-end: 12px;
  }

  &__brand-divider {
    width: 1px;
    height: 20px;
    background-color: $wrapper--mds_brand_appearance_neutral_weak_border-color;
    flex-shrink: 0;
    margin-inline-end: 12px;
  }

  &__app-name {
    font-family: var(--mds_brand_typography_headline_font-family);
    font-size: var(--mds_brand_typography_headline_x-small_desktop_font-size);
    font-weight: var(--mds_brand_typography_headline_medium_font-weight);
    line-height: 1.3;
    color: $wrapper--mds_brand_appearance_neutral_default_text-color;
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__breadcrumb-sep {
    font-family: var(--mds_brand_typography_headline_font-family);
    font-size: var(--mds_brand_typography_headline_x-small_desktop_font-size);
    font-weight: var(--mds_brand_typography_headline_medium_font-weight);
    color: $wrapper--mds_brand_appearance_neutral_weak_text-color;
    margin-inline: 8px;
    flex-shrink: 0;
  }

  &__page-title {
    font-family: var(--mds_brand_typography_headline_font-family);
    font-size: var(--mds_brand_typography_headline_x-small_desktop_font-size);
    font-weight: var(--mds_brand_typography_headline_medium_font-weight);
    line-height: 1.3;
    color: $wrapper--mds_brand_appearance_neutral_weak_text-color;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding-inline-end: 8px;
    gap: 4px;
  }

  &__right-divider {
    width: 1px;
    height: 20px;
    background-color: $wrapper--mds_brand_appearance_neutral_weak_border-color;
    flex-shrink: 0;
    margin-inline: 4px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-header__hamburger {
    transition: none;

    &:active {
      transform: none;
    }
  }
}
</style>
